import React, {useEffect, useRef, useState} from "react";
import { Collapse, Spin, Tabs, Popover } from "antd";
import {generateClassName, getEllipsisText, ObjTest, removeHTML} from "jzfz-web-utils";
import {CommentIcon, ExpandAllIcon, NavFilledIcon} from "../icon/Icon";
import {ModuleEnum} from "../../config/constant";
import {
    StandardSection,
    TypicalProblemSection,
    RulesProblemSection,
    LearnDataSection,
    StandardTaskSection,
    StandardMapSection,
    ExamSection,
} from './Section';
import {
    EyeFilled,
} from '@ant-design/icons';
import {request} from "../../util/request";
import {GLOBAL_PERSONAL_CENTER_API} from "../../config/api";
import CustomEmpty from "../customEmpty/CustomEmpty";
import axios from "axios";
import {COMMENT_AUTO_SCROLL_TOP} from "../../config/storage";
import ViewHistoryPopover from "../toolbar/ViewHistoryPopover";
import './style/content.less';
import {getCrumbsTitle} from "../../util/common";
import { getArticleURL, getArticleInfo } from '../../api/commonAPI';


// 关联数据字段枚举
export enum ConnectNamesEnum {
    ARTICLE_LIST = 'articleList',   // 国家规范
    LOCAL_STANDARD = 'localStandard',   // 地方标准
    INDUSTRY_STANDARD = 'industryStandard', // 行业标准
    REFERENCE_BOOKS = 'referenceBooks', // 参考书籍
    OTHERS_TANDARD = 'otherStandard',   // 其它标准
    REGULATION_LIST = 'regulationList', // 规定问题
    PROBLEM_LIST = 'problemList',   // 典型问题
    MATERIAL_LIST = 'materialList', // 学习资料
    TASK_LIST = 'taskList', // 标准任务
    MAP_PAGE_LIST = 'mapPageList',  // 标准图集
    QUESTION_LIST = 'questionList', // 相关题目
}
const { TabPane } = Tabs;
const { Panel } = Collapse;
const CancelToken = axios.CancelToken;


export interface ConnectModuleExtraPanel {
    title?: string; // 标题
    disabled?: boolean; // 是否禁用
    children?: React.ReactNode; // 内容
    forceRender?: boolean; // 被隐藏时是否渲染 DOM 结构
}


export interface ConnectModuleProps {
    className?: string;
    extraPanel?: ConnectModuleExtraPanel[]; // 扩展面板集合
    tabBarExtraContent?: React.ReactNode;   // tab bar 上额外的元素
    hideOnEmpty?: boolean;  // 没有数据时隐藏组件
    tabKey?: string;
    onTabClick?: (key: string) => void;    // 点击tab
    onVisible?: (visible: boolean) => void; // 显隐变化时触发
    dataSource?: any;   // 关联的数据
    loading?: boolean;
    standardPkid?: string;  // 当前国家规范pkid
}


/**
 * 关联模块
 * @param props
 * @constructor
 */
const ConnectModule = (props: ConnectModuleProps) => {
    const {
        className = '',
        extraPanel,
        tabBarExtraContent,
        hideOnEmpty = true,
        tabKey,
        dataSource = {},
        loading = false,
        standardPkid,
        onTabClick = (key: string) => {},
        onVisible = (visible: boolean) => {},
    } = props;
    const [tabActiveKey, setTabActiveKey] = useState<string>(null);
    // 国家规范详情列表
    const [standardList, setStandardList] = useState<any[]>([]);
    const [standardKeys, setStandardKeys] = useState<any[]>([]);
    // 典型问题列表
    const [qualityProblemList, setQualityProblemList] = useState<any[]>([]);
    const [qualityProblemKeys, setQualityProblemKeys] = useState<any[]>([]);
    // 规定问题详情列表
    const [rulesProblemList, setRulesProblemList] = useState<any[]>([]);
    const [rulesProblemKeys, setRulesProblemKeys] = useState<any[]>([]);
    // 学习资料
    const [learnDataList, setLearnDataList] = useState<any>([]);
    const [learnDataKeys, setLearnDataKeys] = useState<any[]>([]);
    // 标准任务
    const [standardTaskList, setStandardTaskList] = useState<any>([]);
    const [standardTaskKeys, setStandardTaskKeys] = useState<any[]>([]);
    // 标准图集
    const [standardMapList, setStandardMapList] = useState<any>([]);
    const [standardMapKeys, setStandardMapKeys] = useState<any[]>([]);
    // 相关题目
    const [examQuestionList, setExamQuestionList] = useState<any>([]);
    const [examQuestionKeys, setExamQuestionKeys] = useState<any[]>([]);
    // 本地标准
    const [localStandardList, setLocalStandardList] = useState<any[]>([]);
    const [localStandardKeys, setLocalStandardKeys] = useState<any[]>([]);
    // 其它标准
    const [otherStandardList, setOtherStandardList] = useState<any[]>([]);
    const [otherStandardKeys, setOtherStandardKeys] = useState<any[]>([]);
    // 参考书籍
    const [referenceList, setReferenceList] = useState<any[]>([]);
    const [referenceKeys, setReferenceKeys] = useState<any[]>([]);
    // 行业标准
    const [industryStandardList, setIndustryStandardList] = useState<any[]>([]);
    const [industryStandardKeys, setIndustryStandardKeys] = useState<any[]>([]);
    // tabs显隐
    const [visible, setVisible] = useState<boolean>(false);
    // 评论列表
    const [commentList, setCommentList] = useState<any[]>([]);
    // 取消评论列表请求
    const queryCancelRef = useRef<any>(null);


    useEffect(() => {
        return () => {
            if (queryCancelRef.current) {
                queryCancelRef.current();
            }
        }
    }, [])


    useEffect(() => {
        convertData();
        if (tabKey === undefined) {
            let names = tabs.map((v: any) => v.dataIndex);
            getDefaultTabActiveKey(dataSource, names);
        }
    }, [dataSource])


    useEffect(() => {
        if (tabKey !== undefined) {
            setTabActiveKey(tabKey);
        }
    }, [tabKey])


    /**
     * 数据预处理
     */
    const convertData = () => {
        const {
            articleList = [],
            problemList = [],
            regulationList = [],
            materialList = [],
            taskList = [],
            mapPageList = [],
            questionList = [],
            localStandard = [],
            otherStandard = [],
            referenceBooks = [],
            industryStandard = [],
        } = dataSource || {};
        // 控制tabs显隐
        let keys = Object.keys(dataSource);
        let visible = keys.some((v: any) => ObjTest.isNotEmptyArray(dataSource[v]));
        setVisible(visible);
        onVisible(visible);
        // 国家规范
        setStandardList(groupBy(articleList, 'standardPkid', (node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)));
        setStandardKeys(getDirectIndex(articleList));
        // 典型问题
        setQualityProblemList(problemList || []);
        setQualityProblemKeys((problemList || []).map((v: any, i: number) => i));
        // 规定问题
        setRulesProblemList(regulationList || []);
        setRulesProblemKeys(getDirectIndex(regulationList));
        // 学习资料
        setLearnDataList(groupBy(materialList, 'packageId', (node: any) => node.packageName));
        setLearnDataKeys(getDirectIndex(materialList));
        // 标准任务
        setStandardTaskList(taskList || []);
        setStandardTaskKeys(getDirectIndex(taskList));
        // 标准图集
        setStandardMapList(groupBy(mapPageList, 'mapId', (node: any) => (`${node.mapCode} ${node.mapName}`)));
        setStandardMapKeys(getDirectIndex(mapPageList));
        // 相关题目
        setExamQuestionList(questionList || []);
        setExamQuestionKeys(getDirectIndex(questionList));
        // 本地标准
        setLocalStandardList(groupBy(localStandard, 'standardPkid', (node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)));
        setLocalStandardKeys(getDirectIndex(localStandard));
        // 其它标准
        setOtherStandardList(groupBy(otherStandard, 'standardPkid', (node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)));
        setOtherStandardKeys(getDirectIndex(otherStandard));
        // 参考数据
        setReferenceList(groupBy(referenceBooks, 'standardPkid', (node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)));
        setReferenceKeys(getDirectIndex(referenceBooks));
        // 行业标准
        setIndustryStandardList(groupBy(industryStandard, 'standardPkid', (node: any) => (`${node.standardName} ${node.standardCode ? node.standardCode : ''}`)));
        setIndustryStandardKeys(getDirectIndex(industryStandard));
    }


    /**
     * 获取直接关联数据的下标
     * @param list
     */
    const getDirectIndex = (list: any[]): number[] => {
        if (!ObjTest.isNotEmptyArray(list)) {
            return [];
        }
        let tempArr = [];
        list.forEach((v: any, i: number) => {
            if (v.isDirect) {
                tempArr.push(i);
            }
        })
        return tempArr;
    }


    /**
     * 分组
     * @param list
     * @param keyName
     * @param callback
     */
    const groupBy = (list: any[], keyName: string, callback: (node: any) => string) => {
        // 将直接、间接关联的分开
        let directList = list.filter((v: any) => v.isDirect);
        let inDirectList = list.filter((v: any) => !v.isDirect);
        let tempList = [];

        // 直接关联的分组（可以不连续）
        directList.forEach((v: any) => {
            if (tempList.length === 0) {
                tempList.push([v]);
                return;
            }
            let lastList = tempList.slice(-1)[0];
            const [head] = lastList;
            if (v[keyName] === head[keyName]) {
                lastList.push(v);
                return;
            }
            tempList.push([v]);
        })

        tempList.forEach((v: any) => {
            if (ObjTest.isNotEmptyArray(v) && v[0]) {
                v[0].groupName = callback(v[0]);
            }
        })

        // 间接关联的分组(连续)
        let keys = inDirectList.map((v: any) => v[keyName]);
        let uniqueKeys = [];
        keys.forEach((v: string) => {
            if (!uniqueKeys.includes(v)) {
                uniqueKeys.push(v);
            }
        })
        let filterList = uniqueKeys.map((v: string) => {
            return inDirectList.filter((item: any) => item[keyName] === v);
        })
        filterList.forEach((v: any) => {
            if (ObjTest.isNotEmptyArray(v) && v[0]) {
                v[0].groupName = callback(v[0]);
            }
        })
        tempList = [...tempList, ...filterList];

        return tempList.reduce((a: any[], c: any[]) => {
            return [...a, ...c]
        }, [])
    }


    /**
     * 获取默认选中tab的key
     * 默认选中第一个内容不为空的tab
     */
    const getDefaultTabActiveKey = (data: any, keys: string[]) => {
        let index = keys.findIndex((v: any) => ObjTest.isNotEmptyArray(data[v]));
        if (index > -1) {
            setTabActiveKey(index.toString());
        }
    }


    /**
     * 打开新页面
     * @param pkid
     * @param type
     * @param e
     */
    const toNewWindow = async (pkid: any, type: ModuleEnum, e: any) => {
        if (e) {
            e.stopPropagation();
        }
        const { url } = await getArticleInfo({appType: type, pkid});
        window.open(url, '_blank');
    }


    /**
     * 获取关联数据条数
     * @param data
     */
    const getCount = (data: any[]): number => {
        return (data || []).length;
    }


    /**
     * 展示评论
     */
    const showComment = (type: ModuleEnum, pkid: string) => {
        if (queryCancelRef.current) {
            queryCancelRef.current();
        }
        request('/comments/list',
            {platformEnum: '', appType: type, knowledgePkId: pkid},
            {
                method: 'get',
                baseURL: GLOBAL_PERSONAL_CENTER_API,
                cancelToken: new CancelToken((c: any) => queryCancelRef.current = c)
            }
        ).then((res: any) => {
            setCommentList((res.data || []).slice(0, 2));
        })
    }


    /**
     * 打开指定知识页面
     * @param e
     * @param type
     * @param pkid
     */
    const openNewPage = async (e: any, type: ModuleEnum, pkid: string) => {
        e.stopPropagation();
        let url = await getArticleURL(type, pkid);
        // 将新页面的评论设置为自动滚动置顶
        sessionStorage.setItem(COMMENT_AUTO_SCROLL_TOP, JSON.stringify(true));
        window.open(url, '_blank');
        sessionStorage.removeItem(COMMENT_AUTO_SCROLL_TOP);
    }


    const operations = () => {
        return (
            <div
                className="collapse-btn"
                onClick={() => {
                    let keys = [];
                    for (let i = 0; i < tabs.length; i++) {
                        let item = tabs[i];
                        if (tabActiveKey === i.toString()) {
                            keys = item.keys.length === 0 ? item.dataSource.map((v: any, i: number) => i) : [];
                            item.updateKeys(keys);
                            break;
                        }
                    }
                }}
            >
                <ExpandAllIcon />
                <span className="ml8 f12">展开/收起</span>
            </div>
        )
    }


    const getExtra = (moduleType: ModuleEnum, pkid: string, keys: (number | string)[], key: number | string, info: any) => {
        return (
            <>
                <ViewHistoryPopover
                    pkid={pkid}
                    moduleType={moduleType}
                >
                    <span
                        className={generateClassName(['ml24', 'view-more-btn', keys.includes(key) ? 'active' : ''])}
                        title="浏览记录"
                        onClick={(e: any) => e.stopPropagation()}
                    >
                        <EyeFilled className="mr4" />{info.viewCount || 0}
                    </span>
                </ViewHistoryPopover>
                <Popover
                    overlayClassName="connect-content-comment-popover"
                    mouseEnterDelay={0.3}
                    content={(
                        <div className="comment-list-warp" onClick={(e: any) => e.stopPropagation()}>
                            <div className="comment-list">
                                {
                                    ObjTest.isNotEmptyArray(commentList) ? (
                                        commentList.map((v: any, i: number) => (
                                            <div className="comment-list-item" key={i}>
                                                <div className="comment-user">
                                                    <span title={v.createUserName}>{v.createUserName}</span>
                                                    <span title={v.createUserCompanyName}>{v.createUserCompanyName}</span>
                                                </div>
                                                <div className="comment-content" title={v.content}>{v.content}</div>
                                            </div>
                                        ))
                                    ) : (
                                        <CustomEmpty text="暂无评论" />
                                    )
                                }
                            </div>
                            <div className="mta tc">
                                <span
                                    className="view-all-btn"
                                    onClick={(e: any) => openNewPage(e, moduleType, pkid)}
                                >{ObjTest.isNotEmptyArray(commentList) ? '查看全部评论' : '去评论'}
                                </span>
                            </div>
                        </div>
                    )}
                    onVisibleChange={() => showComment(moduleType, pkid)}
                >
                     <span
                         className={generateClassName(['ml24 view-more-btn', keys.includes(key) ? 'active' : ''])}
                         title="查看评论"
                         onClick={(e: any) => openNewPage(e, moduleType, pkid)}
                     >
                        <CommentIcon className="mr4" />{info.commentCount || 0}
                     </span>
                </Popover>
                <span
                    className={generateClassName(['ml24 view-more-btn', keys.includes(key) ? 'active' : ''])}
                    onClick={(e: any) => toNewWindow(pkid, moduleType, e)}
                >
                    <NavFilledIcon className="mr4" />新页面打开
                </span>
            </>
        )
    }


    /**
     * 国家规范
     * @param list 国家规范集合
     * @param keys  展开的key集合
     * @param onChange 更新展开keys的回调函数
     * @param options 额外配置
     */
    const getStandard = (list: any[] = standardList, keys: string[], onChange = (keys: any[]) => {}, options?: {label: string}): React.ReactNode => {
        const { label } = options || {};

        return (
            <Collapse
                className="custom-collapse standard-item"
                activeKey={keys}
                onChange={(keys: string[]) => {
                    onChange(keys.map((key: string) => Number(key)));
                }}
            >
                {
                    (list || []).map((v: any, i: number) => (
                        <React.Fragment key={i}>
                            {
                                v.groupName && (
                                    <div className="item-group">
                                        {label && <span className="standard-type-label">{label}</span>}
                                        {v.groupName}
                                    </div>
                                )
                            }
                            <Panel
                                className={generateClassName([v.isRepeal ? 'invalid-panel' : ''])}
                                header={(
                                    <>
                                        {getCrumbsTitle([`${v.standardName} ${v.standardCode || ''}`, v.fullPathName, v.articleCode])}
                                        {v.isRepeal && <span className="invalid-label">已作废</span>}
                                    </>
                                )}
                                key={i}
                                extra={getExtra(ModuleEnum.standard, v.articlePkid, keys, i, v)}
                            >
                                <StandardSection data={v} standardPkid={standardPkid} />
                            </Panel>
                        </React.Fragment>
                    ))
                }
            </Collapse>
        )
    }


    /**
     * 典型问题
     */
    const getTypicalProblem = (): React.ReactNode => {
        return (
            <Collapse
                className="custom-collapse"
                activeKey={qualityProblemKeys}
                onChange={(keys: string[]) => {
                    setQualityProblemKeys(keys.map((key: string) => Number(key)));
                }}
            >
                {
                    qualityProblemList.map((v: any, i: number) => (
                        <Panel
                            className={generateClassName(['quality-problem-item', v.isRepeal ? 'invalid-panel' : ''])}
                            header={(
                                <>
                                    {`编号：${v.problemNumber}`}
                                    {v.isRepeal && <span className="invalid-label">已作废</span>}
                                </>
                            )}
                            key={i}
                            extra={getExtra(ModuleEnum.typical, v.pkid, qualityProblemKeys, i, v)}
                        >
                            <TypicalProblemSection data={v} />
                        </Panel>
                    ))
                }
            </Collapse>
        )
    }


    /**
     * 规定问题
     */
    const getRulesProblem = (): React.ReactNode => {
        return (
            <Collapse
                className="custom-collapse"
                activeKey={rulesProblemKeys}
                onChange={(keys: string[]) => {
                    setRulesProblemKeys(keys.map((key: string) => Number(key)));
                }}
            >
                {
                    rulesProblemList.map((v: any, i: number) => (
                        <Panel
                            className={generateClassName([v.isRepeal ? 'invalid-panel' : ''])}
                            header={(
                                <>
                                    {v.fullUrl}
                                    {v.isRepeal && <span className="invalid-label">已作废</span>}
                                </>
                            )}
                            key={i}
                            extra={getExtra(ModuleEnum.rules, v.regulationPkid, rulesProblemKeys, i, v)}
                        >
                            <RulesProblemSection data={v} />
                        </Panel>
                    ))
                }
            </Collapse>
        )
    }


    /**
     * 学习资料
     */
    const getLearnData = (): React.ReactNode => {
        return (
            <Collapse
                className="custom-collapse"
                activeKey={learnDataKeys}
                onChange={(keys: string[]) => {
                    setLearnDataKeys(keys.map((key: string) => Number(key)));
                }}
            >
                {
                    learnDataList.map((v: any, i: number) => (
                        <React.Fragment key={i}>
                            { v.groupName ? <div className="item-group">{v.groupName}</div> : null }
                            <Panel
                                className={generateClassName([v.isRepeal ? 'invalid-panel' : ''])}
                                header={(
                                    <>
                                        {v.name}
                                        {v.isRepeal && <span className="invalid-label">已作废</span>}
                                    </>
                                )}
                                key={i}
                                extra={getExtra(ModuleEnum.learn, v.pkid, learnDataKeys, i, v)}
                            >
                                <LearnDataSection data={v} />
                            </Panel>
                        </React.Fragment>
                    ))
                }
            </Collapse>
        )
    }


    /**
     * 标准任务
     */
    const getStandardTask = (): React.ReactNode => {
        return (
            <Collapse
                className="custom-collapse"
                activeKey={standardTaskKeys}
                onChange={(keys: string[]) => {
                    setStandardTaskKeys(keys.map((key: string) => Number(key)));
                }}
            >
                {
                    standardTaskList.map((v: any, i: number) => (
                        <Panel
                            className={generateClassName([v.isRepeal ? 'invalid-panel' : ''])}
                            header={(
                                <>
                                    {v.name}
                                    {v.isRepeal && <span className="invalid-label">已作废</span>}
                                </>
                            )}
                            key={i}
                            extra={getExtra(ModuleEnum.standardTask, v.pkId, standardTaskKeys, i, v)}
                        >
                            <StandardTaskSection data={v} />
                        </Panel>
                    ))
                }
            </Collapse>
        )
    }


    const getStandardMap = () => {
        return (
            <Collapse
                className="custom-collapse"
                activeKey={standardMapKeys}
                onChange={(keys: string[]) => {
                    setStandardMapKeys(keys.map((key: string) => Number(key)));
                }}
            >
                {
                    standardMapList.map((v: any, i: number) => (
                        <React.Fragment key={i}>
                            { v.groupName ? <div className="item-group">{v.groupName}</div> : null }
                            <Panel
                                key={i}
                                className={generateClassName([v.isRepeal ? 'invalid-panel' : ''])}
                                header={(
                                    <div
                                        className="flexic"
                                        title={`${v.mapCode} ${v.mapName} ${v.fullNamePath} ${v.figureNumber}/${v.pageCode}`}
                                    >
                                        <span className="eps1">{v.fullNamePath}{!v.figureNumber ? ` | 索引页 | ${v.pageCode}` : ''}</span>
                                        {
                                            v.figureNumber ? (
                                                <span className="standard-map-block-code">
                                                    <i>{v.figureNumber}</i>
                                                    <i>{v.pageCode}</i>
                                                </span>
                                            ) : null
                                        }
                                        {v.isRepeal && <span className="invalid-label">已作废</span>}
                                    </div>
                                )}
                                extra={getExtra(ModuleEnum.standardMap, v.pkid, standardMapKeys, i, v)}
                            >
                                <StandardMapSection data={v} />
                            </Panel>
                        </React.Fragment>
                    ))
                }
            </Collapse>
        )
    }


    const getExamQuestion = () => {
        return (
            <Collapse
                className="custom-collapse"
                activeKey={examQuestionKeys}
                onChange={(keys: string[]) => {
                    setExamQuestionKeys(keys.map((key: string) => Number(key)));
                }}
            >
                {
                    examQuestionList.map((v: any, i: number) => (
                        <Panel
                            className={generateClassName([v.isRepeal ? 'invalid-panel' : ''])}
                            header={(
                                <>
                                    {getEllipsisText(removeHTML(v.content), 20)}
                                    {v.isRepeal && <span className="invalid-label">已作废</span>}
                                </>
                            )}
                            key={i}
                        >
                            <ExamSection data={v} />
                        </Panel>
                    ))
                }
            </Collapse>
        )
    }


    const tabs = [
        // 国家规范
        {
            title: '国标',
            dataIndex: ConnectNamesEnum.ARTICLE_LIST,
            dataSource: standardList,
            keys: standardKeys,
            updateKeys: setStandardKeys,
            getContent: () => getStandard(standardList, standardKeys, setStandardKeys),
        },
        {
            title: '地标',
            dataIndex: ConnectNamesEnum.LOCAL_STANDARD,
            dataSource: localStandardList,
            keys: localStandardKeys,
            updateKeys: setLocalStandardKeys,
            getContent: () => getStandard(localStandardList, localStandardKeys, setLocalStandardKeys, {label: '地标'}),
        },
        {
            title: '行标',
            dataIndex: ConnectNamesEnum.INDUSTRY_STANDARD,
            dataSource: industryStandardList,
            keys: industryStandardKeys,
            updateKeys: setIndustryStandardKeys,
            getContent: () => getStandard(industryStandardList, industryStandardKeys, setIndustryStandardKeys, {label: '行标'}),
        },
        {
            title: '参考书籍',
            dataIndex: ConnectNamesEnum.REFERENCE_BOOKS,
            dataSource: referenceList,
            keys: referenceKeys,
            updateKeys: setReferenceKeys,
            getContent: () => getStandard(referenceList, referenceKeys, setReferenceKeys, {label: '参考书籍'}),
        },
        {
            title: '其它标准',
            dataIndex: ConnectNamesEnum.OTHERS_TANDARD,
            dataSource: otherStandardList,
            keys: otherStandardKeys,
            updateKeys: setOtherStandardKeys,
            getContent: () => getStandard(otherStandardList, otherStandardKeys, setOtherStandardKeys, {label: '其它标准'}),
        },
        {
            title: '规定问题',
            dataIndex: ConnectNamesEnum.REGULATION_LIST,
            dataSource: rulesProblemList,
            keys: rulesProblemKeys,
            updateKeys: setRulesProblemKeys,
            getContent: () => getRulesProblem(),
        },
        {
            title: '典型问题',
            dataIndex: ConnectNamesEnum.PROBLEM_LIST,
            dataSource: qualityProblemList,
            keys: qualityProblemKeys,
            updateKeys: setQualityProblemKeys,
            getContent: () => getTypicalProblem(),
        },
        {
            title: '学习资料',
            dataIndex: ConnectNamesEnum.MATERIAL_LIST,
            dataSource: learnDataList,
            keys: learnDataKeys,
            updateKeys: setLearnDataKeys,
            getContent: () => getLearnData(),
        },
        {
            title: '标准任务',
            dataIndex: ConnectNamesEnum.TASK_LIST,
            dataSource: standardTaskList,
            keys: standardTaskKeys,
            updateKeys: setStandardTaskKeys,
            getContent: () => getStandardTask(),
        },
        {
            className: "standard-map-tabpane-wrap",
            title: '图集',
            dataIndex: ConnectNamesEnum.MAP_PAGE_LIST,
            dataSource: standardMapList,
            keys: standardMapKeys,
            updateKeys: setStandardMapKeys,
            getContent: () => getStandardMap(),
        },
        {
            title: '考题',
            dataIndex: ConnectNamesEnum.QUESTION_LIST,
            dataSource: examQuestionList,
            keys: examQuestionKeys,
            updateKeys: setExamQuestionKeys,
            getContent: () => getExamQuestion(),
        },
    ]


    /**
     * 获取排序后的tabs
     * 按字段枚举排序
     */
    const getSortedTabs = (): any[] => {
        const fields = Object.values(ConnectNamesEnum);
        return tabs.sort((a: any, b: any) => {
            return fields.findIndex((v: string) => v === a.dataIndex) - fields.findIndex((v: string) => v === b.dataIndex);
        })
    }


    return (
        <div
            className={generateClassName(['connect-module-wrapper', className])}
            hidden={hideOnEmpty && !ObjTest.isNotEmptyArray(extraPanel) && !visible}
        >
            <div className="connect-body">
                <Tabs
                    className="connect-tabs"
                    type="card"
                    activeKey={tabActiveKey}
                    tabBarExtraContent={tabBarExtraContent || operations()}
                    onTabClick={(key: string) => {
                        setTabActiveKey(key);
                        onTabClick(key);
                    }}
                >
                    {
                        getSortedTabs().map((v: any, i: number) => (
                            <TabPane
                                className={generateClassName([v.className])}
                                tab={(
                                    <span
                                        title={!ObjTest.isNotEmptyArray(v.dataSource) ? '无该类型相关知识' : v.title}
                                    >{v.title}({getCount(v.dataSource)})
                                    </span>
                                )}
                                key={i.toString()}
                                disabled={!ObjTest.isNotEmptyArray(v.dataSource)}
                            >
                                {v.getContent()}
                            </TabPane>
                        ))
                    }
                    {
                        (extraPanel || []).map((v: any, i: number) => (
                            <TabPane
                                tab={v.title}
                                key={`${(tabs.length + i).toString()}`}
                                disabled={v.disabled}
                                forceRender={v.forceRender}
                            >{v.children}
                            </TabPane>
                        ))
                    }
                </Tabs>
            </div>
            <Spin className="custom-fixed-loading-wrap" spinning={loading} />
        </div>
    )
}


export default ConnectModule;
